import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Sidebar } from "primereact/sidebar";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { ProgressBar } from "primereact/progressbar";

import CodigoEmBarras from "components/CodigoEmBarras";
import ExportToExcel from "components/ExportToExcel";

import { useAuth } from "context/AuthContext";
import { useLoading } from "context/LoadingContext";
import { useNotification } from "context/NotificationContext";

import Api from "utils/Api";
import MaskUtil from "utils/MaskUtil";

const ListaProduto = () => {
  const { setLoading } = useLoading();
  const auth = useAuth();
  const Requicicao = new Api();
  const notify = useNotification();

  const [Lista, setLista] = useState([]);
  const [filters, setFilters] = useState(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [selectedProdutos, setSelectedProdutos] = useState([]);
  const [filteredLista, setFilteredLista] = useState([]);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [firstIndex, setFirstIndex] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [bulkProgress, setBulkProgress] = useState({ total: 0, success: 0, failed: 0, running: false });
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [resultSummary, setResultSummary] = useState({ total: 0, success: 0, failed: 0 });

  const StateDataPage = async () => {
    try {
      setLoading(true);
      const resposta = await Requicicao.Get({
        endpoint: "/Produto",
        config: auth.GetHeaders()
      });
      if (resposta.codigoEstadoHttp === 200) {
        setLista(resposta.objetoResposta);
        setFilteredLista(resposta.objetoResposta);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const prepareExportData = (data) => 
    data.map((item) => ({
      "Grupo Produto": item.grupoProduto || '',
      "Descrição": item.descricao || '',
      "Código": item.codigo || '',
      "Código GTIN/EAN": item.codigoGtinEan || '',
      "SKU": item.sku || '',
      "Valor de Venda": item.valorVenda ? MaskUtil.applyMonetaryMask(item.valorVenda) : '',
      "Valor de Custo": item.valorCusto ? MaskUtil.applyMonetaryMask(item.valorCusto) : ''
    }));

  const InitFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      descricao: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
      },
      codigoGtinEan: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
      },
      sku: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
      },
      codigo: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
      },
      grupoProduto: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
      }
    });
    setGlobalFilterValue("");
  };

  const ClearFilter = () => {
    InitFilters();
  };

  const OnGlobalFilterChange = (e) => {
    try {
      const { value } = e.target;
      const thisFilters = { ...filters };

      thisFilters.global.value = value;

      setFilters(thisFilters);
      setGlobalFilterValue(value);
    } catch (error) {
      console.error(error);
    }
  };

  const DesativarProduto = async (rowData) => {
    try {
      setLoading(true);
      await Requicicao.Delete({
        endpoint: "/Produto",
        params: { id: rowData.id },
        config: auth.GetHeaders()
      });

      notify({
        type: "success",
        message: "Produto desativado."
      });
      StateDataPage();
    } catch (error) {
      console.error(error);
      notify({
        type: "error",
        message: "Erro ao desativar o produto."
      });
    } finally {
      setLoading(false);
    }
  };

  const Confirm = (rowData) => {
    confirmDialog({
      message: "Tem certeza que deseja excluir este produto?",
      header: "Confirmação de Exclusão",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      accept: () => {
        DesativarProduto(rowData);
      },
      reject: () => {},
      acceptLabel: "Sim, Excluir",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary"
    });
  };

  const ConfirmBulk = () => {
    const total = selectedProdutos?.length || 0;
    if (total === 0) return;
    setShowBulkDialog(true);
  };

  const ExecuteBulkDelete = async () => {
    try {
      setLoading(true);
      const total = selectedProdutos.length;
      let success = 0;
      let failed = 0;
      setBulkProgress({ total, success: 0, failed: 0, running: true });
      for (let i = 0; i < total; i += 1) {
        const p = selectedProdutos[i];
        try {
          // Endpoint: DELETE /Produto?id={id}
          // Mantém mapeamento e progresso item a item
          // eslint-disable-next-line no-await-in-loop
          await Requicicao.Delete({
            endpoint: "/Produto",
            params: { id: p.id },
            config: auth.GetHeaders()
          });
          success += 1;
        } catch (err) {
          console.error(err);
          failed += 1;
        }
        // Atualiza progresso sem closures dentro do loop
        setBulkProgress({ total, success, failed, running: true });
        
        // Delay entre requests para não sobrecarregar o servidor
        if (i < total - 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise(resolve => {
            setTimeout(resolve, 500);
          });
        }
      }
      // Notificar com base no estado final
      if (success > 0) {
        notify({ type: "success", message: `${success} produto(s) desativado(s).` });
      }
      if (failed > 0) {
        notify({ type: "warn", message: `${failed} falha(s) ao desativar.` });
      }
      setResultSummary({ total, success, failed });
      setShowResultDialog(true);
      setSelectedProdutos([]);
      StateDataPage();
    } catch (error) {
      console.error(error);
      notify({ type: "error", message: "Erro ao desativar produtos selecionados." });
    } finally {
      setBulkProgress((prev) => ({ ...prev, running: false }));
      setLoading(false);
      setShowBulkDialog(false);
    }
  };

  const selectCurrentPage = () => {
    const pageItems = (filteredLista || []).slice(firstIndex, firstIndex + rowsPerPage);
    setSelectedProdutos((prev) => {
      const map = new Map((prev || []).map((p) => [p.id, p]));
      pageItems.forEach((p) => map.set(p.id, p));
      return Array.from(map.values());
    });
  };

  const selectAllFiltered = () => {
    setSelectedProdutos(filteredLista || []);
  };

  const onHeaderSelectionClick = () => {
    const totalSelected = selectedProdutos?.length || 0;
    if (totalSelected > 0) {
      confirmDialog({
        message: "Deseja desmarcar todas as seleções?",
        header: "Limpar seleção",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sim",
        rejectLabel: "Não",
        acceptClassName: "p-button-danger",
        rejectClassName: "p-button-secondary",
        accept: () => setSelectedProdutos([])
      });
      return;
    }
    confirmDialog({
      message: "Selecionar apenas a página atual ou todos os itens filtrados?",
      header: "Seleção em lote",
      icon: "pi pi-question-circle",
      acceptLabel: "Todos filtrados",
      rejectLabel: "Página atual",
      acceptClassName: "p-button-primary",
      rejectClassName: "p-button-secondary",
      accept: selectAllFiltered,
      reject: selectCurrentPage
    });
  };

  useEffect(() => {
    if (auth.isAuthenticated()) {
      StateDataPage();
    }
    InitFilters();
  }, [auth]);

  const [visibleRight, setVisibleRight] = useState(false);
  const [DataShowItem, setDataShowItem] = useState(null);

  const isRowSelected = (row) => (selectedProdutos || []).some((p) => p.id === row.id);

  const toggleRowSelection = (row, checked) => {
    if (checked) {
      setSelectedProdutos((prev) => {
        const map = new Map((prev || []).map((p) => [p.id, p]));
        map.set(row.id, row);
        return Array.from(map.values());
      });
    } else {
      setSelectedProdutos((prev) => (prev || []).filter((p) => p.id !== row.id));
    }
  };

  return (
    <>
      <ConfirmDialog />
      <div className="card p-3">
        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex flex-row gap-3">
            <Link to="/app/produto/new" className="btn btn-primary">
              Criar
            </Link>
            <Link to="/app/produto/importar" className="btn btn-secondary">
              Importar Produtos
            </Link>
            <Link to="/app/produto/importar-cfop" className="btn btn-secondary">
              Atualizar CFOP
            </Link>
            <Link to="/app/produto/importar-ncm" className="btn btn-secondary">
              Atualizar NCM
            </Link>
            <Button
              type="button"
              icon="pi pi-filter-slash"
              label="Limpar filtro"
              outlined
              onClick={ClearFilter}
            />
            <ExportToExcel data={prepareExportData(Lista)} fileName="Produtos_Exportados" />
          </div>
          <div className="d-flex align-items-center gap-3">
            <span className="p-input-icon-left">
              <i className="pi pi-search" style={{ left: '1rem' }} />
              <InputText
                value={globalFilterValue}
                onChange={OnGlobalFilterChange}
                placeholder="Pesquisa Global"
                style={{ width: '28rem', paddingLeft: '2.75rem' }}
              />
            </span>
            <div className="d-flex align-items-center gap-2">
              <Tag value={`Selecionados: ${selectedProdutos?.length || 0}`} severity={selectedProdutos?.length ? 'info' : 'secondary'} />
            </div>
            <Button
              type="button"
              icon="pi pi-times"
              label="Limpar seleção"
              outlined
              onClick={() => setSelectedProdutos([])}
              disabled={!selectedProdutos || selectedProdutos.length === 0}
            />
            <Button
              type="button"
              icon="pi pi-trash"
              label="Excluir Selecionados"
              severity="danger"
              disabled={!selectedProdutos || selectedProdutos.length === 0}
              onClick={ConfirmBulk}
            />
          </div>
        </div>
        <DataTable
          value={Lista}
          stripedRows
          paginator
          rows={rowsPerPage}
          first={firstIndex}
          tableStyle={{ minWidth: "50rem" }}
          emptyMessage="Nenhum produto encontrado."
          globalFilterFields={["descricao", "codigo", "codigoGtinEan", "sku", "grupoProduto"]}
          filters={filters}
          dataKey="id"
          onFilter={(e) => setFilteredLista(e.filteredValue || [])}
          onPage={(e) => { setFirstIndex(e.first); setRowsPerPage(e.rows); }}
        >
          <Column
            headerStyle={{ width: '3rem' }}
            headerClassName="text-center"
            bodyClassName="text-center"
            header={
              <div className="d-flex justify-content-center">
                <Checkbox
                  inputId="sel_header"
                  checked={(selectedProdutos?.length || 0) > 0}
                  onChange={onHeaderSelectionClick}
                  aria-label="Selecionar página ou todos filtrados; ou limpar seleção"
                  className="ms-1"
                />
              </div>
            }
            body={(rowData) => (
              <div className="d-flex justify-content-center">
                <Checkbox
                  inputId={`sel_${rowData.id}`}
                  checked={isRowSelected(rowData)}
                  onChange={(e) => toggleRowSelection(rowData, e.checked)}
                />
              </div>
            )}
          />
          <Column field="codigo" header="Código" sortable />
          <Column field="descricao" header="Descrição" sortable />
          <Column field="codigoGtinEan" header="Código GTIN/EAN" sortable />
          <Column field="sku" header="SKU" sortable />
          <Column field="grupoProduto" header="Grupo Produto" sortable />
          <Column
            field="cfop"
            header="CFOP"
            body={(rowData) => {
              if (rowData.cfop) {
                return (
                  <div>
                    <span className="badge bg-primary me-1">{rowData.cfop.codigo}</span>
                    <small className="text-muted d-block">{rowData.cfop.descricao}</small>
                  </div>
                );
              }
              return <span className="text-muted">CFOP Padrão</span>;
            }}
            sortable
          />
          <Column
            field="valorVenda"
            header="Valor de Venda"
            body={(rowData) => MaskUtil.applyMonetaryMask(rowData.valorVenda)}
            sortable
          />
          <Column
            field="valorCusto"
            header="Valor de Custo"
            body={(rowData) => MaskUtil.applyMonetaryMask(rowData.valorCusto)}
            sortable
          />
          <Column
            header="Ações"
            body={(rowData) => (
              <div>
                <div className="d-flex flex-row gap-3">
                  <Link
                    to={`/app/produto/editar?id=${rowData.id}`}
                    className="btn btn-tabela"
                  >
                    <i className="bi bi-pencil" />
                  </Link>
                  <Button
                    className="btn btn-tabela"
                    type="button"
                    icon="pi pi-tag"
                    onClick={() => {
                      setDataShowItem(rowData);
                      setVisibleRight(true);
                    }}
                  />
                  <Button
                    className="btn btn-tabela"
                    type="button"
                    icon="pi pi-trash"
                    onClick={() => {
                      Confirm(rowData);
                    }}
                  />
                </div>
              </div>
            )}
          />
        </DataTable>
      </div>
      <Sidebar
        visible={visibleRight}
        position="right"
        onHide={() => setVisibleRight(false)}
      >
        <h2>Dados do Produto</h2>
        {DataShowItem?.codigo !== undefined && (
          <>
            <p>
              <strong>ID:</strong> {DataShowItem.id}
            </p>
            <p>
              <strong>Descrição:</strong> {DataShowItem.descricao}
            </p>
            <p>
              <strong>Código GTIN/EAN:</strong> {DataShowItem.codigoGtinEan}
            </p>
            <p>
              <strong>SKU:</strong> {DataShowItem.sku}
            </p>
            <p>
              <strong>Grupo Produto:</strong> {DataShowItem.grupoProduto}
            </p>
            <p>
              <strong>CFOP:</strong>{" "}
              {DataShowItem.cfop ? (
                <span>
                  <span className="badge bg-primary me-1">{DataShowItem.cfop.codigo}</span>
                  <small className="text-muted d-block">{DataShowItem.cfop.descricao}</small>
                </span>
              ) : (
                <span className="text-muted">CFOP Padrão</span>
              )}
            </p>
            <p>
              <strong>Valor de Venda:</strong> R${" "}
              {MaskUtil.applyMonetaryMask(DataShowItem.valorVenda)}
            </p>
            <p>
              <strong>Valor de Custo:</strong> R${" "}
              {MaskUtil.applyMonetaryMask(DataShowItem.valorCusto)}
            </p>
            <CodigoEmBarras barcodeData={DataShowItem.codigo} />
          </>
        )}
      </Sidebar>
      <Dialog
        header={`Excluir ${selectedProdutos?.length || 0} produto(s)`}
        visible={showBulkDialog}
        style={{ width: '50vw' }}
        onHide={() => setShowBulkDialog(false)}
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button label="Baixar seleção (JSON)" icon="pi pi-download" outlined disabled={bulkProgress.running} onClick={() => {
              try {
                const blob = new Blob([JSON.stringify(selectedProdutos, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `backup_produtos_${selectedProdutos?.length || 0}.json`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                console.error(err);
                notify({ type: 'error', message: 'Falha ao gerar backup.' });
              }
            }} />
            <Button label="Cancelar" icon="pi pi-times" className="p-button-secondary" onClick={() => setShowBulkDialog(false)} disabled={bulkProgress.running} />
            <Button label={bulkProgress.running ? 'Excluindo...' : 'Excluir'} icon="pi pi-trash" className="p-button-danger" onClick={ExecuteBulkDelete} disabled={bulkProgress.running} />
          </div>
        }
      >
        <p>Os itens abaixo serão desativados. Revise antes de confirmar:</p>
        {bulkProgress.running && (
          <div className="mb-3">
            <ProgressBar value={bulkProgress.total ? Math.round(((bulkProgress.success + bulkProgress.failed) / bulkProgress.total) * 100) : 0} />
            <small className="text-muted">
              {`Processando ${bulkProgress.success + bulkProgress.failed} de ${bulkProgress.total} • Sucesso: ${bulkProgress.success} • Falhas: ${bulkProgress.failed}`}
            </small>
          </div>
        )}
        <DataTable value={selectedProdutos} scrollable scrollHeight="50vh" style={{ borderRadius: '0.25rem' }}>
          <Column field="id" header="ID" style={{ width: '8rem' }} />
          <Column field="codigo" header="Código" style={{ width: '12rem' }} />
          <Column field="descricao" header="Descrição" />
          <Column field="sku" header="SKU" style={{ width: '12rem' }} />
          <Column field="codigoGtinEan" header="GTIN/EAN" style={{ width: '14rem' }} />
        </DataTable>
      </Dialog>
      <Dialog
        header={resultSummary.failed === 0 ? 'Produtos excluídos com sucesso' : 'Processo concluído'}
        visible={showResultDialog}
        style={{ width: '30rem' }}
        onHide={() => setShowResultDialog(false)}
        footer={
          <div className="d-flex justify-content-end">
            <Button label="Ok" icon="pi pi-check" onClick={() => setShowResultDialog(false)} autoFocus />
          </div>
        }
      >
        <div className="mb-2">
          <strong>Total selecionados:</strong> {resultSummary.total}
        </div>
        <div className="mb-2 text-success">
          <strong>Excluídos com sucesso:</strong> {resultSummary.success}
        </div>
        {resultSummary.failed > 0 && (
          <div className="mb-2 text-warning">
            <strong>Falhas:</strong> {resultSummary.failed}
          </div>
        )}
      </Dialog>
    </>
  );
};

export default ListaProduto;
